import * as XLSX from "xlsx";
import {
  generateDeviceInfoCell,
  generateDisplayFormatCell,
  generateTransformCell,
  generateModbusRegisterCell,
  generateModbusIPCell,
  generateModbusPortCell,
  generateModbusSlaveIdCell,
  generateModbusMapIDCell,
  generateLoSetpointCell,
  generateLoLoSetpointCell,
  generateHiSetpointCell,
  generateHiHiSetpointCell,
  generateCodeLegendCell,
  generateSyncDirectionCell,
  generateModbusRegisterPLCCell,
  generateModbusRegisterRTUCell,
  generateParentTagCell,
  generateHwioIOTypeCell,
  generateHwioChannelNumberCell,
  generateHwioBoardNumberCell,
} from "./CellGenerators";

const getCellDisplayValue = (
  columnKey,
  row,
  codeLegends,
  allTags,
  lookupValues
) => {
  switch (columnKey) {
    case "DeviceInfo":
      return generateDeviceInfoCell(row, lookupValues);
    case "HwioIOType":
      return generateHwioIOTypeCell(row, lookupValues);
    case "HwioChannelNumber":
      return generateHwioChannelNumberCell(row, lookupValues);
    case "HwioBoardNumber":
      return generateHwioBoardNumberCell(row, lookupValues);
    case "DisplayFormat":
      return generateDisplayFormatCell(row);
    case "Transform":
      return generateTransformCell(row);
    case "ModbusRegister":
      return generateModbusRegisterCell(row, lookupValues);
    case "ModbusIP":
      return generateModbusIPCell(row, lookupValues);
    case "ModbusPort":
      return generateModbusPortCell(row, lookupValues);
    case "ModbusSlave":
      return generateModbusSlaveIdCell(row, lookupValues);
    case "ModbusMapID":
      return generateModbusMapIDCell(row);
    case "LoSetpoint":
      return generateLoSetpointCell(row, lookupValues);
    case "LoLoSetpoint":
      return generateLoLoSetpointCell(row, lookupValues);
    case "HiSetpoint":
      return generateHiSetpointCell(row, lookupValues);
    case "HiHiSetpoint":
      return generateHiHiSetpointCell(row, lookupValues);
    case "CodeLegend":
      return generateCodeLegendCell(row, codeLegends);
    case "SyncDirection":
      return generateSyncDirectionCell(row, lookupValues);
    case "ModbusRegisterPLC":
      return generateModbusRegisterPLCCell(row);
    case "ModbusRegisterRTU":
      return generateModbusRegisterRTUCell(row);
    case "ParentTagIdentifier": {
      const parentTagInfo = generateParentTagCell(row, allTags);
      if (!parentTagInfo) {
        return "";
      }
      return parentTagInfo.resolved
        ? parentTagInfo.tagName
        : parentTagInfo.tagIdentifier;
    }
    default:
      return row[columnKey] ?? "";
  }
};

export const exportToExcel = (
  data,
  columns,
  codeLegends,
  filename = "tag-grid-export",
  allTags = data,
  lookupValues
) => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  const exportColumns = columns.filter(
    (col) => col.key !== "select-row" && col.key !== "Actions" && col.name
  );

  const headers = exportColumns.map((col) => col.name);

  const rows = data.map((row) => {
    return exportColumns.map((col) => {
      const displayValue = getCellDisplayValue(
        col.key,
        row,
        codeLegends,
        allTags,
        lookupValues
      );
      return displayValue;
    });
  });

  const worksheetData = [headers, ...rows];

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  const columnWidths = exportColumns.map((col) => {
    if (col.key === "TagName" || col.key === "Description") {
      return { wch: 25 };
    } else if (col.key === "DeviceInfo" || col.key === "Transform") {
      return { wch: 30 };
    } else if (col.key === "DisplayFormat") {
      return { wch: 20 };
    } else {
      return { wch: 15 };
    }
  });

  if (worksheet && typeof worksheet === "object") {
    worksheet["!cols"] = columnWidths;
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, "Tag Grid");

  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
  const fullFilename = `${filename}-${timestamp}.xlsx`;

  XLSX.writeFile(workbook, fullFilename);
};

export const exportSelectedToExcel = (
  data,
  columns,
  selectedRows,
  codeLegends,
  filename = "selected-tags-export",
  lookupValues
) => {
  if (!selectedRows || selectedRows.size === 0) {
    console.warn("No rows selected for export");
    return;
  }

  const selectedData = data.filter((_, index) => selectedRows.has(index));

  // Pass the full, unfiltered `data` as `allTags` so a Parent Tag can still be resolved by name even
  // if the parent itself wasn't part of this selected export.
  exportToExcel(
    selectedData,
    columns,
    codeLegends,
    filename,
    data,
    lookupValues
  );
};
